import {
  Alert,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useState } from "react";
import type { EvidenceKind } from "../../api/hooks";
import type { Issue } from "../../api/types";
import { useHasRole } from "../../auth";

/**
 * Resolve a data-quality finding (BIO-308 #2).
 *
 * A **must-resolve** finding needs structured remediation evidence and a reason.
 * Before this, Resolve was one click with no confirmation, no reason and no
 * record of what fixed the condition — which is how the April 2026 run cleared
 * two overdue calibrations and a data gap and still reached Approved.
 *
 * An advisory finding keeps the light path: it gates nothing, so it resolves on
 * a reason alone.
 *
 * The options offered per family mirror the server's rules. The server is
 * authoritative either way; matching it here means a reviewer is not invited to
 * fill in a form that is going to be refused.
 */

const EVIDENCE_LABEL: Record<EvidenceKind, string> = {
  calibration_record: "Calibration record",
  upload: "Accepted upload",
  correction: "Approved correction",
  authorized_override: "Authorized override (admin)",
};

const REF_LABEL: Record<EvidenceKind, string> = {
  calibration_record: "Calibration record ID",
  upload: "Upload ID",
  correction: "Correction ID",
  authorized_override: "",
};

/** Evidence kinds that answer a finding of this family, mirroring the BE. */
export function evidenceKindsFor(family: string): EvidenceKind[] {
  if (family === "calibration") return ["calibration_record"];
  if (family === "upload_schema") return ["upload"];
  return ["upload", "correction"];
}

export function ResolveIssueModal({
  issue,
  opened,
  onClose,
  onConfirm,
  pending,
}: {
  issue: Issue;
  opened: boolean;
  onClose: () => void;
  onConfirm: (args: {
    reason: string;
    evidenceKind?: EvidenceKind;
    evidenceRef?: string;
  }) => Promise<void>;
  pending: boolean;
}) {
  const isAdmin = useHasRole("admin");
  const [reason, setReason] = useState("");
  const [kind, setKind] = useState<EvidenceKind | null>(null);
  const [ref, setRef] = useState("");

  const needsEvidence = issue.must_resolve;
  // The override is the deliberate escape hatch for findings no artifact can
  // answer, and it is admin-only server-side. Hiding it from everyone else
  // keeps the choice honest rather than offering a 403.
  const kinds: EvidenceKind[] = [
    ...evidenceKindsFor(issue.family),
    ...(isAdmin ? (["authorized_override"] as EvidenceKind[]) : []),
  ];

  const needsRef = kind !== null && kind !== "authorized_override";
  const reasonValid = reason.trim().length >= 3;
  const evidenceValid = !needsEvidence || (kind !== null && (!needsRef || ref.trim().length > 0));
  const canSubmit = reasonValid && evidenceValid && !pending;

  const close = () => {
    if (pending) return;
    setReason("");
    setKind(null);
    setRef("");
    onClose();
  };

  const submit = async () => {
    try {
      await onConfirm({
        reason: reason.trim(),
        ...(needsEvidence && kind ? { evidenceKind: kind } : {}),
        ...(needsEvidence && needsRef && ref.trim() ? { evidenceRef: ref.trim() } : {}),
      });
    } catch {
      // onConfirm already surfaced the error; keep the form filled in so the
      // reviewer doesn't have to re-enter the reason and evidence.
      return;
    }
    setReason("");
    setKind(null);
    setRef("");
  };

  return (
    <Modal opened={opened} onClose={close} title="Resolve finding" centered>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {issue.source_identifier} — {issue.description}
        </Text>

        {needsEvidence ? (
          <Alert color="orange" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">
              This is a must-resolve finding: it blocks sign-off, so closing it needs a record of
              what actually remediated it.
            </Text>
          </Alert>
        ) : null}

        {needsEvidence ? (
          <Select
            label="Remediation evidence"
            description="What fixed the underlying condition."
            data={kinds.map((k) => ({ value: k, label: EVIDENCE_LABEL[k] }))}
            value={kind}
            onChange={(v) => {
              setKind(v as EvidenceKind | null);
              setRef("");
            }}
            required
            data-autofocus
          />
        ) : null}

        {needsEvidence && needsRef && kind ? (
          <TextInput
            label={REF_LABEL[kind]}
            description="The server checks that this record exists and genuinely answers the finding."
            value={ref}
            onChange={(e) => setRef(e.currentTarget.value)}
            required
          />
        ) : null}

        {needsEvidence && kind === "authorized_override" ? (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">
              An override records that this finding was closed without remediation. It stays visible
              as an override in the audit trail and on the batch.
            </Text>
          </Alert>
        ) : null}

        <Textarea
          label="Reason"
          description="Recorded in the audit log. Minimum 3 characters."
          minRows={2}
          maxRows={5}
          autosize
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          error={reason.length > 0 && !reasonValid ? "Reason must be at least 3 characters" : null}
          required
        />

        <Group justify="flex-end">
          <Button variant="default" onClick={close} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            loading={pending}
            data-tour="issue-resolve-confirm"
          >
            Resolve
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
